import {
  supabaseClient,
} from "../../services/supabase";

import type {
  EmployeeProfile,
  UserRole,
} from "../../auth/types";


export type NovoFuncionarioInput = {
  nome: string;

  loginId: string;

  senha: string;

  cargo: UserRole;
};


export type EditarFuncionarioInput = {
  id: string;

  nome: string;

  loginId: string;

  cargo: UserRole;
};


type CriarFuncionarioResult = {
  success: boolean;

  funcionario: EmployeeProfile;

  message?: string;
};


type GerenciarFuncionarioResult = {
  success: boolean;

  funcionario?: EmployeeProfile;

  message?: string;
};


/*
 * =========================================================
 * TRATAMENTO DE ERRO DAS EDGE FUNCTIONS
 * =========================================================
 *
 * Quando uma Edge Function responde com HTTP 400/403/etc,
 * o Supabase pode colocar a mensagem real dentro da
 * Response disponível em "context".
 *
 * Assim conseguimos mostrar no frontend mensagens como:
 *
 * "Você não pode desativar sua própria conta."
 * "Este ID de acesso já está sendo utilizado."
 * "Não é possível desativar o último administrador ativo."
 */

async function extractFunctionError(
  error: unknown,
  fallback: string,
): Promise<string> {
  if (
    typeof error ===
      "object" &&
    error !== null &&
    "context" in error
  ) {
    const context =
      (
        error as {
          context?: unknown;
        }
      ).context;


    if (
      context instanceof
      Response
    ) {
      try {
        const body =
          await context
            .clone()
            .json() as {
              message?: string;
            };


        if (
          body.message
        ) {
          return String(
            body.message,
          );
        }
      } catch {
        /*
         * Se não for JSON,
         * usamos a mensagem normal.
         */
      }
    }
  }


  if (
    typeof error ===
      "object" &&
    error !== null &&
    "message" in error
  ) {
    const message =
      String(
        (
          error as {
            message?: string;
          }
        ).message ??
          "",
      ).trim();


    if (message) {
      return message;
    }
  }


  return fallback;
}


/*
 * =========================================================
 * LISTAR FUNCIONÁRIOS
 * =========================================================
 */

export async function loadFuncionarios(): Promise<
  EmployeeProfile[]
> {
  const {
    data,
    error,
  } =
    await supabaseClient
      .from(
        "funcionarios",
      )
      .select(
        `
          id,
          nome,
          email,
          login_id,
          cargo,
          ativo
        `,
      )
      .order(
        "nome",
      );


  if (error) {
    throw error;
  }


  return (
    data ??
    []
  ) as EmployeeProfile[];
}


/*
 * =========================================================
 * CRIAR FUNCIONÁRIO
 * =========================================================
 */

export async function criarFuncionario(
  input: NovoFuncionarioInput,
): Promise<EmployeeProfile> {
  const {
    data,
    error,
  } =
    await supabaseClient
      .functions
      .invoke(
        "criar-funcionario",
        {
          body: {
            nome:
              input.nome,

            loginId:
              input.loginId,

            senha:
              input.senha,

            cargo:
              input.cargo,
          },
        },
      );


  if (error) {
    const message =
      await extractFunctionError(
        error,
        "Não foi possível criar o funcionário.",
      );


    throw new Error(
      message,
    );
  }


  if (!data) {
    throw new Error(
      "Não foi possível criar o funcionário.",
    );
  }


  const result =
    data as CriarFuncionarioResult;


  if (
    !result.success
  ) {
    throw new Error(
      result.message ||
        "Não foi possível criar o funcionário.",
    );
  }


  if (
    !result.funcionario
  ) {
    throw new Error(
      "O funcionário foi criado, mas os dados não foram retornados.",
    );
  }


  return result.funcionario;
}


/*
 * =========================================================
 * EDITAR FUNCIONÁRIO
 * =========================================================
 *
 * Atualiza:
 *
 * - nome
 * - ID de acesso
 * - cargo
 *
 * A Edge Function também sincroniza o e-mail interno
 * utilizado pelo Supabase Auth.
 */

export async function editarFuncionario(
  input: EditarFuncionarioInput,
): Promise<EmployeeProfile> {
  const {
    data,
    error,
  } =
    await supabaseClient
      .functions
      .invoke(
        "gerenciar-funcionario",
        {
          body: {
            action:
              "editar",

            funcionarioId:
              input.id,

            nome:
              input.nome,

            loginId:
              input.loginId,

            cargo:
              input.cargo,
          },
        },
      );


  if (error) {
    const message =
      await extractFunctionError(
        error,
        "Não foi possível editar o funcionário.",
      );


    throw new Error(
      message,
    );
  }


  const result =
    data as
      | GerenciarFuncionarioResult
      | null;


  if (
    !result ||
    !result.success
  ) {
    throw new Error(
      result?.message ||
        "Não foi possível editar o funcionário.",
    );
  }


  if (
    !result.funcionario
  ) {
    throw new Error(
      "O funcionário foi atualizado, mas os dados não foram retornados.",
    );
  }


  return result.funcionario;
}


/*
 * =========================================================
 * ATIVAR / DESATIVAR FUNCIONÁRIO
 * =========================================================
 */

export async function alterarStatusFuncionario(
  funcionarioId: string,
  ativo: boolean,
): Promise<EmployeeProfile> {
  const {
    data,
    error,
  } =
    await supabaseClient
      .functions
      .invoke(
        "gerenciar-funcionario",
        {
          body: {
            action:
              "alterar_status",

            funcionarioId,

            ativo,
          },
        },
      );


  if (error) {
    const message =
      await extractFunctionError(
        error,
        ativo
          ? "Não foi possível ativar o funcionário."
          : "Não foi possível desativar o funcionário.",
      );


    throw new Error(
      message,
    );
  }


  const result =
    data as
      | GerenciarFuncionarioResult
      | null;


  if (
    !result ||
    !result.success
  ) {
    throw new Error(
      result?.message ||
        (
          ativo
            ? "Não foi possível ativar o funcionário."
            : "Não foi possível desativar o funcionário."
        ),
    );
  }


  if (
    !result.funcionario
  ) {
    throw new Error(
      "O status foi alterado, mas os dados do funcionário não foram retornados.",
    );
  }


  return result.funcionario;
}


/*
 * =========================================================
 * REDEFINIR SENHA
 * =========================================================
 */

export async function redefinirSenhaFuncionario(
  funcionarioId: string,
  novaSenha: string,
): Promise<void> {
  const {
    data,
    error,
  } =
    await supabaseClient
      .functions
      .invoke(
        "gerenciar-funcionario",
        {
          body: {
            action:
              "redefinir_senha",

            funcionarioId,

            novaSenha,
          },
        },
      );


  if (error) {
    const message =
      await extractFunctionError(
        error,
        "Não foi possível redefinir a senha.",
      );


    throw new Error(
      message,
    );
  }


  const result =
    data as
      | GerenciarFuncionarioResult
      | null;


  if (
    !result ||
    !result.success
  ) {
    throw new Error(
      result?.message ||
        "Não foi possível redefinir a senha.",
    );
  }
}